import { Label, Input } from "@/components/forms";
import { useTranslation } from "react-i18next";
import { Typography } from "../Typography";
import { TAppliedFilters } from "./FilterPopover";
import { useCallback, useEffect, useMemo } from "react";

type TUserPermissionRangeCountProps = {
  filterState: TAppliedFilters;
  updateFilterState: (value: Partial<TAppliedFilters>) => void;
  id?: string;
  isError: boolean;
  onerror: (value: boolean) => void;
};

const UserPermissionRangeCount = ({
  filterState,
  updateFilterState,
  id,
  isError,
  onerror,
}: TUserPermissionRangeCountProps) => {
  const { t } = useTranslation(["roleManagementPage"]);

  // Validate ranges with deferred values
  const obj = useMemo(() => {
    return {
      permissionMin: filterState?.permissionCountMin,
      permissionMax: filterState?.permissionCountMax,
      userMin: filterState?.userCountMin,
      userMax: filterState?.userCountMax,
    };
  }, [
    filterState?.permissionCountMax,
    filterState?.permissionCountMin,
    filterState?.userCountMax,
    filterState?.userCountMin,
  ]);

  // Validate ranges when deferred values change
  // Validate ranges with deferred values
  const validateRanges = useCallback(() => {
    // Auto-fill missing values
    if (obj?.permissionMin && !obj?.permissionMax) {
      updateFilterState({ permissionCountMax: "1000" });
    }
    if (obj?.userMin && !obj?.userMax) {
      updateFilterState({ userCountMax: "1000" });
    }
    if (!obj?.permissionMin && obj?.permissionMax) {
      updateFilterState({ permissionCountMin: "0" });
    }
    if (!obj?.userMin && obj?.userMax) {
      updateFilterState({ userCountMin: "0" });
    }
    const finalPermMin =
      obj?.permissionMin ?? (obj?.permissionMax !== undefined ? 0 : undefined);
    const finalUserMin =
      obj?.userMin ?? (obj?.userMax !== undefined ? 0 : undefined);
    const finalPermMax =
      obj?.permissionMax ??
      (obj?.permissionMin !== undefined ? 1000 : undefined);
    const finalUserMax =
      obj?.userMax ?? (obj?.userMin !== undefined ? 1000 : undefined);

    const isPermissionInvalid =
      finalPermMin !== undefined &&
      finalPermMax !== undefined &&
      Number(finalPermMin) > Number(finalPermMax);

    const isUserInvalid =
      finalUserMin !== undefined &&
      finalUserMax !== undefined &&
      Number(finalUserMin) > Number(finalUserMax);
    onerror(isPermissionInvalid || isUserInvalid);
  }, [
    obj?.permissionMin,
    obj?.permissionMax,
    obj?.userMin,
    obj?.userMax,
    onerror,
    updateFilterState,
  ]);

  useEffect(() => {
    validateRanges();
  }, [validateRanges]);
  return (
    <>
      {/* Permission Count Range */}
      <div className="w-full">
        <Label weight={"medium"} aria-label="Permission Count Range">
          {t("roleManagementPage:ROLES.SEARCH_FILTERS.PERMISSION_COUNT_RANGE")}
        </Label>
        <div className="flex gap-2">
          <div className="flex-1">
            <Input
              type="number"
              placeholder={t("roleManagementPage:ROLES.SEARCH_FILTERS.MIN")}
              value={filterState.permissionCountMin}
              onChange={(e) =>
                updateFilterState({
                  permissionCountMin: e.target.value,
                })
              }
              min="0"
              className="w-full"
              id={`${id}-permission-count-min-input`}
              aria-label="Permission count min"
            />
          </div>
          <div className="flex items-center">
            <span className="text-gray-500">
              {t("roleManagementPage:ROLES.SEARCH_FILTERS.TO")}
            </span>
          </div>
          <div className="flex-1">
            <Input
              type="number"
              placeholder={t("roleManagementPage:ROLES.SEARCH_FILTERS.MAX")}
              value={filterState.permissionCountMax}
              onChange={(e) =>
                updateFilterState({
                  permissionCountMax: e.target.value,
                })
              }
              min="0"
              className="w-full"
              id={`${id}-permission-count-max-input`}
              aria-label="Permission count max"
            />
          </div>
        </div>
      </div>

      {/* User Count Range */}
      <div className="w-full">
        <Label weight={"medium"} aria-label="User Count Range">
          {t("roleManagementPage:ROLES.SEARCH_FILTERS.USER_COUNT_RANGE")}
        </Label>
        <div className="flex gap-2">
          <div className="flex-1">
            <Input
              type="number"
              placeholder={t("roleManagementPage:ROLES.SEARCH_FILTERS.MIN")}
              value={filterState.userCountMin}
              onChange={(e) =>
                updateFilterState({
                  userCountMin: e.target.value,
                })
              }
              min="0"
              className="w-full"
              id={`${id}-user-count-min-input`}
              aria-label="User count min"
            />
          </div>
          <div className="flex items-center">
            <span className="text-gray-500">
              {t("roleManagementPage:ROLES.SEARCH_FILTERS.TO")}
            </span>
          </div>
          <div className="flex-1">
            <Input
              type="number"
              placeholder={t("roleManagementPage:ROLES.SEARCH_FILTERS.MAX")}
              value={filterState.userCountMax}
              onChange={(e) =>
                updateFilterState({
                  userCountMax: e.target.value,
                })
              }
              min="0"
              className="w-full"
              id={`${id}-user-count-max-input`}
              aria-label="User count max"
            />
          </div>
        </div>
      </div>

      {/* Error Message */}
      {isError && (
        <div className="w-full lg:col-span-2">
          <Typography variant="small" className="text-red-600 text-sm">
            {t("roleManagementPage:ROLES.SEARCH_FILTERS.MIN_CANNOT_EXCEED_MAX")}
          </Typography>
        </div>
      )}
    </>
  );
};
export default UserPermissionRangeCount;
